import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Flag from './Flag';
import { useJazyk } from '../../context/LanguageContext';

/**
 * Mapa světa pro Bloom World.
 *
 * Není to mapový podklad s ulicemi – je to kreslený tvar světa, který se chová
 * jako navigace. Proto tu není žádná mapová knihovna:
 *   – geometrie je jeden statický soubor (105 kB), který se stáhne až tady,
 *   – d3-geo z něj spočítá tvary a načítá se dynamickým importem, takže
 *     v hlavním balíku programu není,
 *   – vykreslení je obyčejné SVG, takže barvy řídí náš design a ne cizí styl.
 *
 * Mapa nikdy není jediná cesta k obsahu – vedle ní vždy stojí hledání a seznam
 * zemí. Klávesnicí se dá projít zeměmi, ve kterých Bloom něco má; procházet
 * tabulátorem 177 tvarů by nikomu nepomohlo.
 */

// Kam až se dá přiblížit. Víc než pětinásobek nemá u téhle geometrie smysl,
// tvary se začnou lámat.
const ZOOM_MIN = 1;
const ZOOM_MAX = 5;

const VIEW_W = 1000;
const VIEW_H = 500;

const BARVY = {
  none: '#FBFAFE',        // země, kde Bloom zatím nic nemá – skoro bílá
  limited: '#C3B9F0',     // něco málo
  supported: '#8A7CFF',   // rozjeté
  obrys: '#CFC9E6',       // jemný obrys, ať jsou hranice čitelné i u prázdných
  more: '#6B72E8',        // zvýraznění pod kurzorem
  domov: '#3FC9A4',       // domovská země
};

export default function WorldMap({
  countries = [],
  homeCountryIso2 = null,
  onSelectCountry,
  activeCategory = 'all',
  className = '',
}) {
  const [tvary, setTvary] = useState(null);      // [{ iso2, d, name }]
  const { t, dvojjazycne } = useJazyk();
  const [chyba, setChyba] = useState('');
  const [pod, setPod] = useState(null);          // země pod kurzorem/prstem
  const [pohled, setPohled] = useState({ zoom: 1, x: 0, y: 0 });
  const svgRef = useRef(null);
  const tahRef = useRef(null);
  // Žiťe déle než `tahRef`: čte se ještě při `click`, který chodí až po
  // puštění tlačítka. Nuluje se až při dalším stisku.
  const bylTahRef = useRef(false);

  // Údaje o zemích podle kódu – ať se v překreslování nehledá v poli.
  const podleKodu = useMemo(() => {
    const m = new Map();
    countries.forEach((z) => m.set(z.iso2, z));
    return m;
  }, [countries]);

  // Geometrie i knihovna se načtou až tady. Když se komponenta odpojí dřív,
  // výsledek zahodíme, ať se nenastavuje stav odpojené komponenty.
  useEffect(() => {
    let zivy = true;
    (async () => {
      try {
        const [{ geoNaturalEarth1, geoPath }, { feature }, topoOdpoved, prevodOdpoved] =
          await Promise.all([
            import('d3-geo'),
            import('topojson-client'),
            fetch('/assets/geo/countries-110m.json'),
            fetch('/assets/geo/numeric-to-iso2.json'),
          ]);
        if (!topoOdpoved.ok || !prevodOdpoved.ok) throw new Error(t('mapa.chybaNacteni'));

        const topo = await topoOdpoved.json();
        const naIso2 = await prevodOdpoved.json();
        const geo = feature(topo, topo.objects.countries);
        // Antarktida se vynechává: nikdo tam Bloom hledat nebude a v projekci
        // zabírá spodní třetinu, takže by zbytek světa vyšel zbytečně malý.
        const bezAntarktidy = {
          ...geo,
          features: geo.features.filter((f) => String(f.id) !== '10' && String(f.id) !== '010'),
        };
        const projekce = geoNaturalEarth1().fitSize([VIEW_W, VIEW_H], bezAntarktidy);
        const kresli = geoPath(projekce);

        const hotove = bezAntarktidy.features
          .map((f) => ({
            iso2: naIso2[String(f.id).padStart(3, '0')] || null,
            name: f.properties?.name || '',
            d: kresli(f),
          }))
          .filter((t) => t.d);

        if (zivy) setTvary(hotove);
      } catch (e) {
        if (zivy) setChyba(t('mapa.chybaZkusteSeznam'));
      }
    })();
    return () => { zivy = false; };
  }, []);

  const barvaZeme = useCallback((iso2) => {
    const z = iso2 ? podleKodu.get(iso2) : null;
    if (!z) return BARVY.none;
    if (activeCategory !== 'all') {
      // Při filtru se zvýrazňují jen země, kde daná věc opravdu je.
      return (z.counts?.[activeCategory] || 0) > 0 ? BARVY.supported : BARVY.none;
    }
    return BARVY[z.content_status] || BARVY.none;
  }, [podleKodu, activeCategory]);

  const klik = useCallback((iso2) => {
    const z = iso2 ? podleKodu.get(iso2) : null;
    if (z && onSelectCountry) onSelectCountry(z);
  }, [podleKodu, onSelectCountry]);

  // ── Posun a přiblížení ────────────────────────────────────────────────────
  const zoomuj = useCallback((smer, stred) => {
    setPohled((p) => {
      const novy = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, p.zoom * (smer > 0 ? 1.25 : 0.8)));
      if (novy === p.zoom) return p;
      // Přiblížení kolem bodu pod kurzorem, ne kolem rohu.
      const s = stred || { x: VIEW_W / 2, y: VIEW_H / 2 };
      const pomer = novy / p.zoom;
      return {
        zoom: novy,
        x: s.x - (s.x - p.x) * pomer,
        y: s.y - (s.y - p.y) * pomer,
      };
    });
  }, []);

  const naSouradnice = (e) => {
    const r = svgRef.current?.getBoundingClientRect();
    if (!r) return null;
    return { x: ((e.clientX - r.left) / r.width) * VIEW_W, y: ((e.clientY - r.top) / r.height) * VIEW_H };
  };

  const dolu = (e) => {
    tahRef.current = {
      x: e.clientX, y: e.clientY, puvod: { ...pohled },
      tahnuto: false, id: e.pointerId, zachyceno: false,
    };
    bylTahRef.current = false;
    // Ukazatel se schválně **nezachytává** hned. Zachycený ukazatel totiž
    // přesměruje i následnou událost `click` na to, co ho zachytilo – tedy na
    // celé SVG – a obsluha kliknutí na jednotlivou zemi by se nespustila.
  };

  const pohyb = (e) => {
    const t = tahRef.current;
    if (!t) return;
    const r = svgRef.current?.getBoundingClientRect();
    if (!r) return;
    const dx = ((e.clientX - t.x) / r.width) * VIEW_W;
    const dy = ((e.clientY - t.y) / r.height) * VIEW_H;

    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
      t.tahnuto = true;
      bylTahRef.current = true;
      // Teď už o zachycení stojíme: tažení nemá skončit tím, že kurzor sjede
      // mimo mapu. Kliknutí už se to netýká, protože to není kliknutí.
      if (!t.zachyceno) {
        e.currentTarget.setPointerCapture?.(t.id);
        t.zachyceno = true;
      }
    }
    if (!t.tahnuto) return;
    setPohled({ ...t.puvod, x: t.puvod.x + dx, y: t.puvod.y + dy });
  };

  const nahoru = (e) => {
    const t = tahRef.current;
    if (t?.zachyceno) e.currentTarget.releasePointerCapture?.(t.id);
    tahRef.current = null;
  };

  // Tahem se nemá vybírat země – jinak by každé posunutí mapy skončilo výběrem.
  const bylTah = () => bylTahRef.current;

  if (chyba) {
    return (
      <div className={`flex items-center justify-center bg-bloom-bg rounded-3xl border border-border/40 ${className}`}
           style={{ minHeight: 260 }} data-testid="world-map-error">
        <p className="text-sm text-bloom-sub px-6 text-center">{chyba}</p>
      </div>
    );
  }

  if (!tvary) {
    return (
      <div className={`flex items-center justify-center bg-bloom-bg rounded-3xl border border-border/40 ${className}`}
           style={{ minHeight: 260 }} data-testid="world-map-loading">
        <div className="spinner" />
      </div>
    );
  }

  const zemeSObsahem = (iso2) => (podleKodu.get(iso2)?.total || 0) > 0;

  return (
    <div className={`relative ${className}`} data-testid="world-map">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        className="w-full h-full touch-none select-none"
        role="group"
        aria-label={t('mapa.popis')}
        onPointerDown={dolu}
        onPointerMove={pohyb}
        onPointerUp={nahoru}
        onPointerCancel={nahoru}
        onWheel={(e) => zoomuj(e.deltaY < 0 ? 1 : -1, naSouradnice(e))}
        style={{ cursor: 'grab' }}
      >
        <defs>
          <radialGradient id="bloom-more" cx="50%" cy="45%" r="75%">
            <stop offset="0%" stopColor="#E7EFFC" />
            <stop offset="100%" stopColor="#DCE6F8" />
          </radialGradient>
        </defs>

        {/* Moře. Bez něj by pevnina plavala v prázdnu. */}
        <rect x={-VIEW_W} y={-VIEW_H} width={VIEW_W * 3} height={VIEW_H * 3} fill="url(#bloom-more)" />

        <g transform={`translate(${pohled.x} ${pohled.y}) scale(${pohled.zoom})`}>
          {tvary.map((tvar, i) => {
            const zvyrazneno = pod && tvar.iso2 && pod === tvar.iso2;
            const domov = tvar.iso2 && tvar.iso2 === homeCountryIso2;
            const klikatelne = !!tvar.iso2;
            return (
              <path
                key={tvar.iso2 || `x${i}`}
                d={tvar.d}
                fill={zvyrazneno ? BARVY.more : barvaZeme(tvar.iso2)}
                stroke={domov ? BARVY.domov : BARVY.obrys}
                strokeWidth={domov ? 2 : 0.6}
                vectorEffect="non-scaling-stroke"
                role={klikatelne ? 'button' : undefined}
                tabIndex={klikatelne && zemeSObsahem(tvar.iso2) ? 0 : -1}
                aria-label={klikatelne ? (dvojjazycne(podleKodu.get(tvar.iso2)?.name_cs, podleKodu.get(tvar.iso2)?.name_en) || tvar.name) : undefined}
                onPointerEnter={() => tvar.iso2 && setPod(tvar.iso2)}
                onPointerLeave={() => setPod(null)}
                onClick={() => { if (!bylTah()) klik(tvar.iso2); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); klik(tvar.iso2); }
                }}
                style={{ transition: 'fill 140ms ease-out', outline: 'none' }}
                className={klikatelne ? 'focus-visible:stroke-bloom-violet' : ''}
              />
            );
          })}
        </g>
      </svg>

      {/* Ovládání přiblížení – na dotykových zařízeních se hodí i bez gest */}
      <div className="absolute right-3 bottom-3 flex flex-col gap-1.5">
        <button type="button" onClick={() => zoomuj(1)} aria-label={t('mapa.priblizit')}
                className="w-9 h-9 rounded-xl bg-white/90 border border-border/50 text-bloom-text text-lg leading-none shadow-sm hover:bg-white">+</button>
        <button type="button" onClick={() => zoomuj(-1)} aria-label={t('mapa.oddalit')}
                className="w-9 h-9 rounded-xl bg-white/90 border border-border/50 text-bloom-text text-lg leading-none shadow-sm hover:bg-white">−</button>
        <button type="button" onClick={() => setPohled({ zoom: 1, x: 0, y: 0 })} aria-label={t('mapa.celySvet')}
                className="w-9 h-9 rounded-xl bg-white/90 border border-border/50 text-bloom-sub text-xs shadow-sm hover:bg-white">⤢</button>
      </div>

      {/* Popisek země pod kurzorem */}
      {pod && podleKodu.get(pod) && (
        <div className="absolute left-3 top-3 bg-white/95 backdrop-blur rounded-2xl border border-border/50 shadow-sm px-3.5 py-2.5 pointer-events-none"
             data-testid="world-map-tooltip">
          <div className="flex items-center gap-2">
            <Flag emoji={podleKodu.get(pod).flag} iso2={pod} size="md" />
            <span className="font-heading font-bold text-bloom-text">{dvojjazycne(podleKodu.get(pod).name_cs, podleKodu.get(pod).name_en)}</span>
          </div>
          <PocetyObsahu counts={podleKodu.get(pod).counts} total={podleKodu.get(pod).total} />
        </div>
      )}
    </div>
  );
}

// Druhy obsahu, které se počítají. Tvary slov jsou ve slovníku pod
// `obsah.<druh>` – v každém jazyce se skloňují jinak.
export const DRUHY_OBSAHU = ['specialists', 'organizations', 'events', 'pageants', 'resources'];

function PocetyObsahu({ counts = {}, total = 0 }) {
  const { t } = useJazyk();
  if (!total) {
    return <p className="text-xs text-bloom-sub/80 mt-0.5">{t('mapa.teprveRoste')}</p>;
  }
  return (
    <ul className="mt-1 space-y-0.5">
      {Object.entries(counts).filter(([, n]) => n > 0).map(([klic, n]) => {
        const slovo = DRUHY_OBSAHU.includes(klic) ? t(`obsah.${klic}`, { pocet: n }) : klic;
        return <li key={klic} className="text-xs text-bloom-sub">{n} {slovo}</li>;
      })}
    </ul>
  );
}
